import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  Chip,
  OutlinedInput,
  FormControl,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiManager } from "../Services/Networking";
import AppNavbar from "../Components/AppNavbar";
import ColorModeIconDropdown from "../theme/ColorModeIconDropdown";
import { X } from "lucide-react";
import Layout from "../Components/Layout";

export default function SoftwareAccessForm() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const isEdit = !!state?.group;
  const fullList = state?.fullList || [];

  const [formData, setFormData] = useState({
    name: state?.group?.name || "",
    ats_employee: state?.group?.ats_employee || false,
    ats_web_admin: state?.group?.ats_web_admin || false,
    ats_web_hr: state?.group?.ats_web_hr || false,
    hrm_admin: state?.group?.hrm_admin || false,
    hrm_hr: state?.group?.hrm_hr || false,
  });

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialIds, setInitialIds] = useState([]);

  useEffect(() => {
    if (isEdit && state.group.employees) {
      const ids = state.group.employees.map((e) => e.id);
      setSelectedEmployees(ids);
      setInitialIds(ids);
    }
    const fetchEmployees = async () => {
      const res = await ApiManager.getEmployees();
      if (res.status === "success") setEmployeeList(res.data);
    };
    fetchEmployees();
  }, [isEdit, state?.group]);

  const handleSave = async () => {
    setLoading(true);
    const res = isEdit
      ? await ApiManager.updateSoftwareAccess(state.group.id, formData)
      : await ApiManager.createSoftwareAccess(formData);

    if (res.status === "success") {
      const groupId = isEdit ? state.group.id : res.data.id;

      const toAdd = selectedEmployees.filter((id) => !initialIds.includes(id));
      const toRemove = initialIds.filter(
        (id) => !selectedEmployees.includes(id),
      );

      if (toAdd.length > 0 || toRemove.length > 0) {
        await ApiManager.updateSoftwareEmployees(groupId, {
          ...(toAdd.length > 0 && { add: toAdd }),
          ...(toRemove.length > 0 && { delete: toRemove }),
        });
      }

      // Map the response 'name' to 'software_access_name' to match the DataGrid column
      const updatedGroup = {
        ...res.data,
        software_access_name: res.data.name || formData.name,
        employees: selectedEmployees.map((id) => ({
          id,
          name: employeeList.find((e) => e.emp_id === id)?.emp_name || id,
        })),
      };

      const updatedList = isEdit
        ? fullList.map((g) =>
            g.id === groupId ? { ...g, ...updatedGroup } : g,
          )
        : [...fullList, updatedGroup];

      navigate("/software-access", { state: { updatedList }, replace: true });
    }
    setLoading(false);
  };

  return (
    <Layout name={isEdit ? "EDIT SOFTWARE ACCESS" : "ADD SOFTWARE ACCESS"}>
      <Stack direction="row" justifyContent="flex-end" spacing={2} mb={4}>
        <Button
          onClick={() =>
            navigate("/software-access", {
              state: { updatedList: fullList },
            })
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isEdit ? "Update" : "Save"}
        </Button>
      </Stack>
      <Grid container spacing={3} padding={2}>
        <Grid size={12}>
          <Typography mb={1} fontWeight="medium">
            Group Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
        </Grid>

        {[
          "ats_employee",
          "ats_web_admin",
          "ats_web_hr",
          "hrm_admin",
          "hrm_hr",
        ].map((key) => (
          <Grid size={12} md={4} key={key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData[key]}
                  onChange={(e) =>
                    setFormData({ ...formData, [key]: e.target.checked })
                  }
                />
              }
              label={key.replace(/_/g, " ").toUpperCase()}
            />
          </Grid>
        ))}

        <Grid size={12}>
          <Typography mb={1} fontWeight="medium">
            Assign Employees
          </Typography>
          <FormControl fullWidth>
            <Select
              multiple
              value={selectedEmployees}
              onChange={(e) => setSelectedEmployees(e.target.value)}
              input={<OutlinedInput size="small" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((id) => (
                    <Chip
                      key={id}
                      label={id}
                      size="small"
                      onDelete={() =>
                        setSelectedEmployees((prev) =>
                          prev.filter((i) => i !== id),
                        )
                      }
                      deleteIcon={<X size={14} />}
                      onMouseDown={(e) => e.stopPropagation()}
                    />
                  ))}
                </Box>
              )}
            >
              {employeeList.map((emp) => (
                <MenuItem key={emp.emp_id} value={emp.emp_id}>
                  {emp.emp_name} ({emp.emp_id})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Layout>
  );
}
